import React from 'react'

import clsx from 'clsx'

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label: string
  placeholder: string
  inputSize?: 'small' | 'medium' | 'large'
  disabled?: boolean
  error?: string
  leadingIcon?: React.ReactNode
  trailingIcon?: React.ReactNode
  helperText?: string
  hiddenLabel?: boolean
  addOn?: string | React.ReactNode
  addOnPosition?: 'left' | 'right'
  isPill?: boolean
  cornerHint?: string
  isRequired?: boolean
}

export const Input = ({
  label,
  placeholder,
  value,
  disabled = false,
  error,
  leadingIcon,
  trailingIcon,
  helperText,
  type = 'text',
  hiddenLabel = false,
  inputSize = 'medium',
  addOn,
  addOnPosition = 'left',
  isPill = false,
  cornerHint = '',
  isRequired = false,
  ...props
}: InputProps) => {
  const sizeClasses = {
    small: 'px-2 py-1 text-sm',
    medium: 'px-4 py-2 text-xs font-normal',
    large: 'px-6 py-2 text-lg',
  }

  return (
    <div className="flex w-full flex-col">
      <div className="flex justify-between">
        {label && !hiddenLabel && (
          <label className="mb-1 text-xs font-normal">
            {label}
            {isRequired && <span className="ml-1 text-red-600">*</span>}
          </label>
        )}
        {cornerHint && <span className="text-sm text-gray-500">{cornerHint}</span>}
      </div>
      <div className={clsx('relative w-full', addOn && 'flex')}>
        {addOn && addOnPosition === 'left' && (
          <span className="inline-flex items-center rounded-l-md border border-r-0 border-gray-300 px-3 text-gray-500 sm:text-sm">
            {addOn}
          </span>
        )}
        {leadingIcon && <div className="absolute inset-y-0 left-0 flex items-center pl-3">{leadingIcon}</div>}
        <input
          id={props.id}
          name={props.name}
          type={type}
          placeholder={placeholder}
          disabled={disabled}
          value={value}
          className={clsx(
            'block w-full border-0 text-gray-900 shadow-sm ring-1 ring-gray-900/20 placeholder:text-gray-400 focus:ring-2 focus:ring-inset',
            sizeClasses[inputSize],
            isPill ? 'rounded-full' : 'rounded-md',
            leadingIcon && 'pl-10',
            trailingIcon && 'pr-10',
            addOn && addOnPosition === 'left' && 'rounded-l-none',
            addOn && addOnPosition === 'right' && 'rounded-r-none',
            props.className
          )}
          {...props}
        />
        {trailingIcon && <div className="absolute inset-y-0 right-0 flex items-center pr-3">{trailingIcon}</div>}
        {addOn && addOnPosition === 'right' && (
          <span className="inline-flex items-center rounded-r-md border border-l-0 border-gray-300 px-3  text-gray-500 sm:text-sm">
            {addOn}
          </span>
        )}
      </div>
      {helperText && <p className={clsx('mt-2 text-sm', error ? 'text-red-600' : 'text-gray-500')}>{helperText}</p>}
    </div>
  )
}
